// Vertical-slice automatic abilities (bible values; plan §18).

import type { AbilityDef } from './types';

export const ABILITIES: AbilityDef[] = [
  {
    id: 'ab-lock-shields',
    name: 'Lock Shields',
    trigger: { when: 'targetedByRangedSquads', count: 2 },
    internalCooldown: 20,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [
      { fx: 'statMod', stat: 'rangedDamageTaken', mult: 0.65, duration: 6 },
      { fx: 'statMod', stat: 'moveSpeed', mult: 0.75, duration: 6 },
    ],
    ui: { label: 'Lock Shields', showFloatingLabel: true },
  },
  {
    id: 'ab-finish-the-line',
    name: 'Finish the Line',
    trigger: { when: 'enemyBelowHealth', fraction: 0.25 },
    internalCooldown: 12,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'attackSpeed', mult: 1.15, duration: 4 }],
    ui: { label: 'Finish the Line', showFloatingLabel: true },
  },
  {
    id: 'ab-brace',
    name: 'Brace',
    trigger: { when: 'chargedBy', sizeTags: ['large', 'giant'] },
    internalCooldown: 15,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'damageTaken', mult: 0.5, duration: 4 }],
    ui: { label: 'Brace', showFloatingLabel: true },
  },
  {
    id: 'ab-impaling-hedge',
    name: 'Impaling Hedge',
    trigger: { when: 'largeEnemyInRange', range: 5 },
    internalCooldown: 18,
    telegraphSeconds: 0.4,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'damage', amount: 240 },
      { fx: 'slow', magnitude: 0.35, duration: 4 },
    ],
    ui: { label: 'Impaling Hedge', showFloatingLabel: true },
  },
  {
    id: 'ab-giant-mark',
    name: 'Giant Mark',
    trigger: { when: 'attackingTargetTag', tag: 'boss' },
    internalCooldown: 12,
    telegraphSeconds: 0.3,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'mark', markId: 'giant-mark', duration: 8 }],
    ui: { label: 'Giant Mark', showFloatingLabel: true },
  },
  {
    id: 'ab-captains-guard',
    name: "Captain's Guard",
    trigger: { when: 'selfBelowHealth', fraction: 0.5, oncePerLife: true },
    internalCooldown: 9999,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'rangedDamageTaken', mult: 0.6, duration: 6 }],
    ui: { label: "Captain's Guard", showFloatingLabel: true },
  },
  {
    // bible IC-12 Unbroken Standard: aura reapplied on a short timer. The fear/
    // displacement resistance is folded into a small damage-taken reduction (deviation).
    id: 'ab-unbroken-standard',
    name: 'Unbroken Standard',
    trigger: { when: 'timer', period: 3 },
    internalCooldown: 3,
    telegraphSeconds: 0,
    targetSelector: 'self',
    effects: [
      { fx: 'auraBuff', radius: 10, stat: 'moveSpeed', mult: 1.1, duration: 3.2 },
      { fx: 'auraBuff', radius: 10, stat: 'damageTaken', mult: 0.95, duration: 3.2 },
    ],
    ui: { label: 'Unbroken Standard', showFloatingLabel: false },
  },
  {
    // bible IC-12 Rally the Fallen: fires when an allied squad dies nearby (squad-pool
    // model approximation of "loses half its members" — deviation).
    id: 'ab-rally-the-fallen',
    name: 'Rally the Fallen',
    trigger: { when: 'allyDiedNearby', range: 10 },
    internalCooldown: 40,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 10, stat: 'attackSpeed', mult: 1.2, duration: 8 }],
    ui: { label: 'Rally the Fallen', showFloatingLabel: true },
  },
  {
    // bible IC-24 Saint's Rebuke: tower-shield slam that scatters surrounding infantry
    id: 'ab-saints-rebuke',
    name: "Saint's Rebuke",
    trigger: { when: 'surroundedBy', count: 3, range: 4, sizeTags: ['small', 'medium'] },
    internalCooldown: 18,
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'areaDamage', amount: 60, radius: 4 },
      { fx: 'knockback', distance: 5 },
    ],
    ui: { label: "Saint's Rebuke", showFloatingLabel: true },
  },
  {
    // bible IC-25 Kingbreaker Hammer: every 4th structure hit is an overhead strike
    // that also halves repairs for 5 s
    id: 'ab-kingbreaker-hammer',
    name: 'Kingbreaker Hammer',
    trigger: { when: 'nthAttackVsStructure', n: 4 },
    internalCooldown: 2,
    telegraphSeconds: 0.5,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'structureStrike', damage: 600, repairHalfSeconds: 5 }],
    ui: { label: 'Kingbreaker', showFloatingLabel: true },
  },

  // ------------------------------------------------- v0.2.4 roster completion
  {
    // bible IC-03 Pavise Stakes: "stand still 4 s → 20% ranged resistance until moving".
    // No stationary condition in the vocabulary — fires when ranged fire arrives instead
    // (Lock Shields pattern), for a fixed duration (deviation).
    id: 'ab-pavise-stakes',
    name: 'Pavise Stakes',
    trigger: { when: 'targetedByRangedSquads', count: 1 },
    internalCooldown: 20, // TEST
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'rangedDamageTaken', mult: 0.8, duration: 6 }],
    ui: { label: 'Pavise Stakes', showFloatingLabel: true },
  },
  {
    // bible IC-03 Focused Bolts: ramping armor penetration on a held target — no armor-pen
    // stat; approximated as a damage buff once the target is worn down (deviation).
    id: 'ab-focused-bolts',
    name: 'Focused Bolts',
    trigger: { when: 'enemyBelowHealth', fraction: 0.5 },
    internalCooldown: 14, // TEST
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'damage', mult: 1.15, duration: 4 }],
    ui: { label: 'Focused Bolts', showFloatingLabel: true },
  },
  {
    // bible IC-08 Tempered Blessing: 20% physical resist on the most damaged nearby
    // armored squad — no single-ally buff target; approximated as a periodic
    // damage-taken aura (deviation).
    id: 'ab-tempered-blessing',
    name: 'Tempered Blessing',
    trigger: { when: 'timer', period: 12 },
    internalCooldown: 12,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 8, stat: 'damageTaken', mult: 0.8, duration: 7 }],
    ui: { label: 'Tempered Blessing', showFloatingLabel: false },
  },
  {
    // bible IC-08 Field Mend: out-of-combat machine repair / squad healing → periodic
    // lowest-ally heal (Mend the Wounded pattern); works in combat too (deviation).
    id: 'ab-field-mend',
    name: 'Field Mend',
    trigger: { when: 'timer', period: 4 },
    internalCooldown: 4,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 55, range: 10 }], // TEST
    ui: { label: 'Field Mend', showFloatingLabel: false },
  },
  {
    // bible IC-09 Breakthrough Charge: minimum-travel impact → a forced cavalry dash.
    id: 'ab-breakthrough-charge',
    name: 'Breakthrough Charge',
    trigger: { when: 'enemyInRange', range: 14, minRange: 4 },
    internalCooldown: 14, // TEST
    telegraphSeconds: 0.4,
    targetSelector: 'triggeringEntity',
    effects: [{
      fx: 'charge',
      speedMult: 3.2,
      durationSeconds: 1.8,
      impactDamage: 140, // TEST — ~60% bonus over a normal swing
      impactKnockback: 2.2,
      impactKnockdownSeconds: 1.2,
    }],
    ui: { label: 'Breakthrough', showFloatingLabel: true },
  },
  {
    // owner request 2026-07-14: mounted cavalry should close distance with a real charge
    // instead of only applying a dive hit or teleport approximation.
    id: 'ab-cavalry-charge',
    name: 'Cavalry Charge',
    trigger: { when: 'enemyInRange', range: 14, minRange: 4 },
    internalCooldown: 16, // TEST
    telegraphSeconds: 0.35,
    targetSelector: 'triggeringEntity',
    effects: [{
      fx: 'charge',
      speedMult: 3.1,
      durationSeconds: 1.8,
      impactDamage: 85, // TEST
      impactKnockback: 1.6,
      impactKnockdownSeconds: 1.0,
    }],
    ui: { label: 'Cavalry Charge', showFloatingLabel: true },
  },
  {
    // bible IC-09 Wheel and Reform: post-charge circling → speed surge when the squad
    // takes real damage, i.e. when it most needs to disengage (deviation).
    id: 'ab-wheel-and-reform',
    name: 'Wheel and Reform',
    trigger: { when: 'selfBelowHealth', fraction: 0.5, oncePerLife: false },
    internalCooldown: 18, // TEST
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'moveSpeed', mult: 1.25, duration: 4 }],
    ui: { label: 'Wheel and Reform', showFloatingLabel: true },
  },
  {
    // bible IC-12 Writ of Condemnation: first attack marks a support/caster and cuts
    // healing — no role-filtered targeting; applies to the current target on a cadence
    // (deviation). Shield-generation reduction folded into the heal reduction.
    id: 'ab-writ-of-condemnation',
    name: 'Writ of Condemnation',
    trigger: { when: 'timer', period: 12 },
    internalCooldown: 12, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'mark', markId: 'writ-of-condemnation', duration: 8 },
      { fx: 'healReduction', mult: 0.65, duration: 8 },
    ],
    ui: { label: 'Writ of Condemnation', showFloatingLabel: true },
  },
  {
    // bible IC-12 Execution Chains: yanks an escaping marked target back — no pull
    // effect; chains instead root and punish a target caught without allies (deviation).
    id: 'ab-execution-chains',
    name: 'Execution Chains',
    trigger: { when: 'targetIsolated', range: 8 },
    internalCooldown: 20, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'damage', amount: 120 }, // TEST
      { fx: 'root', duration: 1.5 },
    ],
    ui: { label: 'Execution Chains', showFloatingLabel: true },
  },
  {
    // bible IC-13 Deploy Aegis Drone: intercepts one projectile aimed at a nearby Large
    // unit, rebuilds in 28 s — no interception effect; approximated as a periodic
    // ranged-damage-reduction aura on the drone's rebuild cadence (deviation).
    id: 'ab-aegis-drone',
    name: 'Aegis Drone',
    trigger: { when: 'timer', period: 28 },
    internalCooldown: 28,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 8, stat: 'rangedDamageTaken', mult: 0.8, duration: 6 }], // TEST
    ui: { label: 'Aegis Drone', showFloatingLabel: true },
  },
  {
    // bible IC-13 Overcharge Engine: +25% attack speed on the nearest mech, then a
    // cooldown slump — single-mech targeting and the slump aren't representable;
    // approximated as a periodic attack-speed aura (deviation).
    id: 'ab-overcharge-engine',
    name: 'Overcharge Engine',
    trigger: { when: 'timer', period: 16 },
    internalCooldown: 16, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 8, stat: 'attackSpeed', mult: 1.25, duration: 7 }],
    ui: { label: 'Overcharge', showFloatingLabel: false },
  },
  {
    // bible IC-14 Stonewall Stance: 25% damage reduction while Defending/stationary —
    // no order/stationary condition; braces when ranged squads focus the pair
    // (deviation). Knockback immunity dropped.
    id: 'ab-stonewall-stance',
    name: 'Stonewall Stance',
    trigger: { when: 'targetedByRangedSquads', count: 2 },
    internalCooldown: 18, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'damageTaken', mult: 0.75, duration: 8 }],
    ui: { label: 'Stonewall Stance', showFloatingLabel: true },
  },
  {
    // bible IC-14 Retaliation Bell: absorb-threshold slam → fires when swarmed by
    // Small/Medium squads (Saint's Rebuke pattern — deviation).
    id: 'ab-retaliation-bell',
    name: 'Retaliation Bell',
    trigger: { when: 'surroundedBy', count: 2, range: 4, sizeTags: ['small', 'medium'] },
    internalCooldown: 20, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'areaDamage', amount: 70, radius: 4 }, // TEST
      { fx: 'knockback', distance: 4 },
    ],
    ui: { label: 'Retaliation Bell', showFloatingLabel: true },
  },
  {
    // bible IC-15 Skyhook Dive: dive-stun with distance-scaled damage → periodic burst
    // + root on the current target (deviation: stun → root, no distance scaling, no
    // ranged/support target filter).
    id: 'ab-skyhook-dive',
    name: 'Skyhook Dive',
    trigger: { when: 'timer', period: 16 },
    internalCooldown: 16, // TEST
    telegraphSeconds: 0.4,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'damage', amount: 130 }, // TEST
      { fx: 'root', duration: 1 },
    ],
    ui: { label: 'Skyhook Dive', showFloatingLabel: true },
  },
  {
    // bible IC-15 Rescue Lift: carrying a dying support squad to safety — allies can't
    // be moved; approximated as an emergency heal of the weakest nearby ally when a
    // squad is lost close by (deviation).
    id: 'ab-rescue-lift',
    name: 'Rescue Lift',
    trigger: { when: 'allyDiedNearby', range: 12 },
    internalCooldown: 30, // TEST
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 180, range: 12 }], // TEST
    ui: { label: 'Rescue Lift', showFloatingLabel: true },
  },
  {
    // bible IC-16 Covered Advance: Smalls beneath the hull take 30% less ranged damage
    // and can't be displaced → short-radius aura reapplied on a timer (Unbroken
    // Standard pattern); applies to all nearby allies, displacement immunity dropped
    // (deviation).
    id: 'ab-covered-advance',
    name: 'Covered Advance',
    trigger: { when: 'timer', period: 3 },
    internalCooldown: 3,
    telegraphSeconds: 0,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 5, stat: 'rangedDamageTaken', mult: 0.7, duration: 3.2 }],
    ui: { label: 'Covered Advance', showFloatingLabel: false },
  },
  {
    // bible IC-16 Gatebreaker: 3 uninterrupted hits charge a massive strike that
    // disables the building's attack — attack-disable isn't representable; every 4th
    // structure hit lands a heavy strike that halves repairs (Kingbreaker pattern —
    // deviation).
    id: 'ab-gatebreaker',
    name: 'Gatebreaker',
    trigger: { when: 'nthAttackVsStructure', n: 4 },
    internalCooldown: 2,
    telegraphSeconds: 0.5,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'structureStrike', damage: 700, repairHalfSeconds: 4 }], // TEST
    ui: { label: 'Gatebreaker', showFloatingLabel: true },
  },
  {
    // bible IC-17 Royal Challenge: duel the strongest nearby non-Giant — marks the
    // Large unit that comes into range; the ±20% damage split is folded into the
    // Exemplars' damageVs.large (deviation).
    id: 'ab-royal-challenge',
    name: 'Royal Challenge',
    trigger: { when: 'largeEnemyInRange', range: 8 },
    internalCooldown: 16, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'triggeringEntity',
    effects: [{ fx: 'mark', markId: 'royal-challenge', duration: 8 }],
    ui: { label: 'Royal Challenge', showFloatingLabel: true },
  },
  {
    // bible IC-17 Last Banner: the last surviving champion plants the banner — squad
    // pools have no member count; fires once at 20% pool health (deviation). Fear
    // immunity dropped (no fear system).
    id: 'ab-last-banner',
    name: 'Last Banner',
    trigger: { when: 'selfBelowHealth', fraction: 0.2, oncePerLife: true },
    internalCooldown: 9999,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'attackSpeed', mult: 1.4, duration: 10 }],
    ui: { label: 'Last Banner', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): siege giants charge to close distance — charge extension
    id: 'ab-ic-ram-charge',
    name: 'Battering Advance',
    trigger: { when: 'enemyInRange', range: 18, minRange: 5 },
    internalCooldown: 30, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'charge', speedMult: 2.5, durationSeconds: 2.5, impactDamage: 130, impactSlowMult: 0.6, impactSlowSeconds: 2 }, // TEST
    ],
    ui: { label: 'Charge', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): humanoid boss giants hoist and crush small squads
    id: 'ab-ic-colossus-crush',
    name: 'Crowning Grasp',
    trigger: { when: 'surroundedBy', count: 1, range: 6, sizeTags: ['small'] },
    internalCooldown: 25, // TEST
    telegraphSeconds: 0.8,
    targetSelector: 'self',
    effects: [{ fx: 'crushGrab', maxTargetSize: 'small', kills: 2, range: 6 }], // TEST
    ui: { label: 'Crush', showFloatingLabel: true },
  },
];
